import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { X, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';

const KEY = 'demo_tour_v1';
export const startManagerTour = () => window.dispatchEvent(new Event('manager-tour:start'));
export const startDemoTour = () => window.dispatchEvent(new Event('demo-tour:start'));

const DEMO_STEPS = [
  { route: '/portal', target: '[data-testid="portal-scope-title"]', title: 'Welcome to the Training Dashboard', body: 'This quick tour shows how training, certifications and SOP compliance are tracked for a customer account. Everything you see is sample data.' },
  { route: '/portal', target: '[data-testid="portal-kpis"]', title: 'Analytics at a glance', body: 'Headcount, training completion, certificates, observation pass rate and SOP acknowledgement — always scoped to the account you are allowed to see.' },
  { route: '/portal', target: '[data-testid="portal-process-progress"]', title: 'Progress by process', body: 'Each operational process (Receiving, Shipping, Returns…) shows how much of its training is complete across the team.' },
  { route: '/portal/associates', target: '[data-testid^="portal-assoc-row-"]', title: 'Associates', body: 'Every associate assigned to the account. Click a row to see their training records and completion dates.' },
  { route: '/admin/skill-matrix', target: '[data-testid="matrix-accounts-bar"]', title: 'Training Matrix · customer accounts', body: 'The matrix is organized by building, then customer account. Each tab shows that account\'s processes and people.' },
  { route: '/admin/skill-matrix', target: '[data-testid="matrix-process-panel"]', title: 'Processes', body: 'Pick a process to see its trainings. Managers build these lists; customers get a read-only view.' },
  { route: '/admin/skill-matrix', target: '[data-testid="matrix-table"]', title: 'Who is trained, and when', body: 'Every cell shows Not Trained, In Progress, Trained or Trainer — with the completion date for audits. Export to Excel anytime.' },
  { route: '/sops', target: '[data-testid="sops-accounts-bar"]', title: 'SOPs & Visual Aids', body: 'Work instructions, SOPs and visual aids, organized by customer account and published by managers.' },
  { route: '/sops', target: '[data-testid^="sop-view-"]', title: 'Preview any document', body: 'PDF, Word, PowerPoint and Excel files open right in the browser, looking just like Office — no downloads or extra software. Next, we\'ll open one for you.' },
  { route: '/sops', target: '[data-testid="sop-preview-dialog"]', before: '[data-testid^="sop-view-"]', title: 'Read-to-acknowledge', body: 'Documents open in the same viewer as Microsoft Office. Associates confirm "I\'ve read this" after reading, and every acknowledgement is recorded as proof of training.' },
];

const MANAGER_STEPS = [
  { route: '/admin', target: '[data-testid="admin-dashboard"] h2', title: 'Welcome, Manager', body: 'This tour walks through everything you can do as a manager. Your view is always limited to your own building.' },
  { route: '/admin', target: '[data-testid="analytics-filters"]', title: 'Building analytics', body: 'Training completion, assessments, PIT licenses, SOP acknowledgement and Training Matrix coverage for your building. Filter by customer account and date range, or export the data.' },
  { route: '/admin/associates', target: '[data-testid="create-associate-btn"]', title: 'Create associate accounts', body: 'Add new associates to your building. They sign in with a temporary password and are asked to choose their own.' },
  { route: '/admin/associates', target: '[data-testid="associate-search"]', title: 'Manage your team', body: 'Search, filter by status or customer account, edit profiles, reset passwords, mark trainers and deactivate people who leave.' },
  { route: '/admin/assignments', target: '[data-testid="assign-btn"]', title: 'Assign training', body: 'Pick associates and modules, set a due date, and they will see it under Assigned Tasks.' },
  { route: '/admin/skill-matrix', target: '[data-testid="matrix-accounts-bar"]', title: 'Training Matrix', body: 'Organised by customer account, then process. Add processes and trainings, and record who is Trained or a Trainer, with completion dates.' },
  { route: '/admin/skill-matrix', target: '[data-testid="matrix-export-btn"]', title: 'Export for audits', body: 'Download the matrix to Excel whenever a customer or auditor asks for proof of training.' },
  { route: '/sops', target: '[data-testid="sops-upload-btn"]', title: 'SOPs & Visual Aids', body: 'Upload PDFs, Word, PowerPoint or Excel files, publish them to associates, and track who has acknowledged each one.' },
  { route: '/admin/observations', target: '[data-testid="new-observation-btn"]', title: 'Observations', body: 'Run on-the-floor observations against process checklists. Save drafts and send associates to retraining when needed.' },
  { route: '/training-signoff', target: '[data-testid="signoff-search"]', title: 'Training Sign-Off', body: 'Sign off process checklists for each associate as they are trained on the floor.' },
  { route: '/admin/pit-licenses', target: '[data-testid="new-pit-license-btn"]', title: 'PIT Licenses', body: 'Issue wallet-style Powered Industrial Truck licenses. Signatures can be collected on a phone by scanning a QR code.' },
  { route: '/shorts', target: '[data-testid="shorts-feed"]', title: 'Training Shorts', body: 'Short training videos for your building. On your phone you can record or upload new Shorts and approve associate comments.' },
  { route: '/admin/kaizens', target: '[data-testid="kaizen-new-btn"]', title: 'Kaizens', body: 'Track continuous-improvement ideas with owners, contributors and due dates.' },
  { route: '/admin/meetings', target: '[data-testid="new-meeting-btn"]', title: 'Meetings', body: 'Log team meetings and attendance. You can replay this tour any time from "Take the tour" in the menu.' },
];

export const TOURS = {
  demo: { steps: DEMO_STEPS, key: KEY, event: 'demo-tour:start' },
  manager: { steps: MANAGER_STEPS, key: 'manager_tour_v1', event: 'manager-tour:start' },
};

function useTargetRect(selector, active) {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    if (!active) return;
    setRect(null);
    let scrolled = false;
    const tick = () => {
      const el = document.querySelector(selector);
      if (!el) return;
      if (!scrolled) { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); scrolled = true; }
      const r = el.getBoundingClientRect();
      setRect(prev => (prev && prev.top === r.top && prev.left === r.left && prev.width === r.width && prev.height === r.height) ? prev : { top: r.top, left: r.left, width: r.width, height: r.height });
    };
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [selector, active]);
  return rect;
}

function TourCard({ step, index, total, rect, onNext, onBack, onClose }) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(360, vw - 24);
  let style = { width: w, left: (vw - w) / 2, top: vh / 2 - 100 };
  if (rect) {
    const big = rect.height > vh * 0.6;
    const below = rect.top + rect.height + 12;
    const top = big ? Math.max(12, vh - 250) : below + 220 < vh ? below : Math.max(12, rect.top - 232);
    style = { width: w, top, left: Math.min(Math.max(12, rect.left), vw - w - 12) };
  }
  const last = index === total - 1;
  return (
    <div data-tour className="fixed z-[10001] rounded-xl bg-white shadow-2xl border border-slate-200 p-5 transition-[top,left] duration-300" style={{ ...style, pointerEvents: 'auto' }} data-testid="demo-tour-card">
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-[#F37021] flex items-center gap-1"><Sparkles size={12} /> Step {index + 1} of {total}</p>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Close tour" data-testid="demo-tour-close"><X size={16} /></button>
      </div>
      <h4 className="mt-1.5 text-lg font-bold text-[#003366]" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="demo-tour-title">{step.title}</h4>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{step.body}</p>
      <div className="mt-3 h-1 rounded-full bg-slate-100 overflow-hidden"><span className="block h-full bg-[#F37021] transition-[width] duration-300" style={{ width: `${((index + 1) / total) * 100}%` }} /></div>
      <div className="mt-4 flex items-center justify-between">
        <button type="button" onClick={onClose} className="text-xs text-slate-400 hover:text-slate-600" data-testid="demo-tour-skip">Skip tour</button>
        <div className="flex gap-2">
          {index > 0 && <Button variant="outline" size="sm" onClick={onBack} data-testid="demo-tour-back"><ArrowLeft size={14} className="mr-1" /> Back</Button>}
          <Button size="sm" className="bg-[#003366] hover:bg-[#002244] text-white" onClick={onNext} data-testid="demo-tour-next">{last ? 'Finish' : <>Next <ArrowRight size={14} className="ml-1" /></>}</Button>
        </div>
      </div>
    </div>
  );
}

export function DemoTour({ enabled, tour = 'demo', autoStart = true, userId = '' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [index, setIndex] = useState(null);
  const { steps: STEPS, event } = TOURS[tour];
  const storeKey = `${TOURS[tour].key}${userId ? `_${userId}` : ''}`;

  const save = useCallback((v) => { try { localStorage.setItem(storeKey, JSON.stringify(v)); } catch { /* ignore */ } }, [storeKey]);
  const close = useCallback(() => {
    setIndex(null);
    save({ done: true });
    document.querySelector('[data-testid="sop-preview-dialog"]') && document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  }, [save]);

  useEffect(() => {
    if (!enabled) return;
    let state = {};
    try { state = JSON.parse(localStorage.getItem(storeKey) || '{}'); } catch { /* ignore */ }
    if (!state.done && (autoStart || state.step !== undefined)) setIndex(state.step || 0);
    const start = () => { setIndex(0); save({ step: 0 }); };
    window.addEventListener(event, start);
    return () => window.removeEventListener(event, start);
  }, [enabled, storeKey, event, autoStart, save]);

  const step = index !== null ? STEPS[index] : null;

  useLayoutEffect(() => {
    if (!step) return;
    save({ step: index });
    if (location.pathname !== step.route) { navigate(step.route); return; }
    if (step.before && !document.querySelector(step.target)) {
      const id = setInterval(() => {
        const el = document.querySelector(step.before);
        if (el) { el.click(); clearInterval(id); }
      }, 250);
      return () => clearInterval(id);
    }
  }, [step, index, location.pathname, navigate, save]);

  const rect = useTargetRect(step?.target || '', !!step && location.pathname === step?.route);
  if (!enabled || !step) return null;

  const go = (d) => {
    const next = index + d;
    if (STEPS[index].before && d < 0) document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    if (next >= STEPS.length) close(); else setIndex(next);
  };
  const pad = 6;
  return createPortal(
    <div data-tour data-testid="demo-tour">
      {rect ? (
        <div className="fixed z-[10000] rounded-xl transition-all duration-300 ring-2 ring-[#F37021]" style={{ top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2, boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.55)', pointerEvents: 'none' }} data-testid="demo-tour-spotlight" />
      ) : <div className="fixed inset-0 z-[10000] bg-slate-900/55" style={{ pointerEvents: 'none' }} />}
      <TourCard step={step} index={index} total={STEPS.length} rect={rect} onNext={() => go(1)} onBack={() => go(-1)} onClose={close} />
    </div>,
    document.body,
  );
}
